/**
 * src/components/pdf/2024/sections/AppendicesPDF.js
 *
 * Appendices — section cover + Appendix A–E + Footnotes.
 *
 * Named exports: AppendicesPages, AppendicesSection
 * Default export: standalone Document (used by the generate script)
 */

import React from 'react';
import { Document, View, Text, Image, Link } from '@react-pdf/renderer';
import { styles, COLORS, registerFonts } from '../styles';
import PageTemplate from '../PageTemplate';
import {
  H1, H3, H4, Body, Small, Section,
  BulletList, Footnote,
  TableCaption, DataTable, TCell, TBold, TBullets,
  SectionCover,
  PageOffsetContext,
} from '../shared';

import appendixCoverImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/appendices/appendix_cover.jpg';
import ghgTotalImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/appendices/appendix-b/total_emissions_annual.png';
import auditsAndAssessmentsImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/appendices/appendix-c/audits_and_assessments.png';
import scorecardGradesImg from '../../../ui/corporateresponsibility/impact/2024-impact-report/appendices/appendix-d/appendix_d_cr_scorecard_grades.png';

registerFonts();

// ---------------------------------------------------------------------------
// Appendices content pages
// ---------------------------------------------------------------------------
export function AppendicesPages() {
  // ── Appendix A — SASB ─────────────────────────────────────────────────────
  const sasbCols = [
    { label: 'Topic', flex: 1.4 },
    { label: 'Metric', flex: 3 },
    { label: 'Category', flex: 1.1 },
    { label: 'Unit of Measure', flex: 1 },
    { label: 'Code', flex: 1 },
    { label: 'Reference / Response', flex: 3 },
  ];
  const sasbRows = [
    [
      'Management of Chemicals in Products',
      'Discussion of processes to maintain compliance with restricted substances regulations',
      'Discussion and Analysis',
      'n/a',
      'CG-AA-250a.1',
      <View>
        <TCell><Link href="/corporate-responsibility/impact/2024/sustaining-places#chemical-management">Chemical Management</Link></TCell>
        <TCell><Link href="https://cscworkday.blob.core.windows.net/hrforms/Recruiting/Career_Site/Standards_Policies_Manuals/CSC_RSL&Product_Safety_Manual_English.pdf">CSC Restricted Substances List (RSL)</Link></TCell>
      </View>,
    ],
    [
      'Management of Chemicals in Products',
      'Discussion of processes to assess and manage risks or hazards associated with chemicals in products',
      'Discussion and Analysis',
      'n/a',
      'CG-AA-250a.2',
      <View>
        <TCell><Link href="/corporate-responsibility/impact/2024/sustaining-places#chemical-management">Chemical Management</Link></TCell>
        <TCell><Link href="https://cscworkday.blob.core.windows.net/hrforms/Recruiting/Career_Site/Standards_Policies_Manuals/CSC_CM_Policy_Guideline_English.pdf">CSC Chemical Management Policy (CMP)</Link></TCell>
      </View>,
    ],
    [
      'Environmental Impacts in the Supply Chain',
      "Percentage of (1) Tier 1 supplier facilities and (2) supplier facilities beyond Tier 1 that have completed the Sustainable Apparel Coalition\u2019s Higg Facility Environmental Module (Higg FEM) assessment or an equivalent environmental data assessment",
      'Quantitative',
      'Percentage (%)',
      'CG-AA-430a.2',
      <View>
        <TCell>In 2024, 258 Tier 1 and 313 Tier 2 supplier facilities completed the Higg FEM Assessment (2023 cadence). 78% of Tier 1 and 64% of Tier 2 supplier facilities completed it, representing 70% of supplier facilities that manufacture CSC products.</TCell>
        <TCell><Link href="/corporate-responsibility/impact/2024/sustaining-places#sustainable-manufacturing">Sustainable Manufacturing</Link></TCell>
      </View>,
    ],
    [
      'Labour Conditions in the Supply Chain',
      'Percentage of (1) Tier 1 supplier facilities and (2) supplier facilities beyond Tier 1 that have been audited to a labour code of conduct, (3) percentage of total audits conducted by a third-party auditor',
      'Quantitative',
      'Percentage (%)',
      'CG-AA-430b.1',
      <View>
        <TCell>(1) 87% of active Tier 1 manufacturing partner facilities had results from at least one audit or assessment analyzed against our Standards of Manufacturing Practices and local labor laws, including audit/assessment results from unannounced audits performed by our internal specialists or designated third party audit firms, assessments performed by the ILO Better Work program, verified assessments using the Social and Labor Convergence ("SLCP") Program's Converged Assessment Framework ("CAF"), and audits performed by external parties against other standards.</TCell>
        <TCell>(2) 12% of Tier 2 manufacturing partner facilities have been audited using the SLCP CAF, and/or audits performed by external parties against other standards with the data analyzed against our SMP.</TCell>
        <TCell>(3) 94% of total audits conducted at Tier 1 manufacturing partner facilities were conducted by third party auditors. 100% of audits conducted at Tier 2 manufacturing partner facilities were conducted by third party auditors using the SLCP CAF.</TCell>
      </View>,
    ],
    [
      'Labour Conditions in the Supply Chain',
      '(1) Priority non-conformance rate and (2) associated corrective action rate for suppliers\' labour code of conduct audits',
      'Quantitative',
      'Rate',
      'CG-AA-430b.2',
      <View>
        <TCell>In 2024, less than 1% of supplier facilities received an F rating. Supplier facilities receiving F ratings were those presenting high-risk, non-compliance issues.</TCell>
        <TCell>Social Responsibility Program and Scorecard Process, see 2024 Impact Report Appendix C.</TCell>
        <TCell><Link href="https://cscworkday.blob.core.windows.net/hrforms/Recruiting/Career_Site/Standards_Policies_Manuals/Standards_Manufacturing_Poster.pdf">Standards of Manufacturing Practices</Link></TCell>
      </View>,
    ],
    [
      'Labour Conditions in the Supply Chain',
      'Description of the greatest (1) labour and (2) environmental, health and safety risks in the supply chain',
      'Discussion and Analysis',
      'n/a',
      'CG-AA-430b.3',
      <View>
        <TCell>See "Risk Factors" in our Fiscal Year 2024 Form 10-K</TCell>
        <TCell><Link href="https://investor.columbia.com/sec-filings/all-sec-filings/content/0001050797-25-000023/colm-20241231.htm?TB_iframe=true&height=auto&width=auto&preload=false">Link to 10-K</Link></TCell>
      </View>,
    ],
    [
      'Raw Materials Sourcing',
      '(1) List of priority raw materials; for each priority raw material: (2) environmental or social factor(s) most likely to threaten sourcing, (3) discussion on business risks or opportunities associated with environmental or social factors and (4) management strategy for addressing business risks and opportunities',
      'Discussion and Analysis',
      'n/a',
      'CG-AA-440a.3',
      <View>
        <TCell>The following represent risks identified, as well as the policies and statements to mitigate these risks.</TCell>
        <TBullets
          items={[
            <Link href="https://cscworkday.blob.core.windows.net/hrforms/Recruiting/Career_Site/Position_Statements/Conflict_Minerals.pdf">Conflict Minerals Policy</Link>,
            <Link href="https://investor.columbia.com/sec-filings/all-sec-filings/content/0001050797-24-000099/0001050797-24-000099.pdf">2023 Conflict Minerals Report</Link>,
            <Link href="https://cscworkday.blob.core.windows.net/hrforms/Recruiting/Career_Site/Position_Statements/Animal_Derived_Materials_Policy.pdf">Animal Derived Policy</Link>,
            <Link href="https://cscworkday.blob.core.windows.net/hrforms/Recruiting/Career_Site/Position_Statements/Natural_Down&Feather.pdf">Natural Down & Feather Insulation Statement</Link>,
            <Link href="https://cscworkday.blob.core.windows.net/hrforms/Recruiting/Career_Site/Position_Statements/Climate_Change.pdf">Statement on Climate Change</Link>,
            <Link href="https://cscworkday.blob.core.windows.net/hrforms/Recruiting/Career_Site/Position_Statements/Uzbekistan&Turkmenistan_Cotton_Statement.pdf">Uzbekistan & Turkmenistan Cotton Policy</Link>,
            <Link href="https://cscworkday.blob.core.windows.net/hrforms/Recruiting/Career_Site/Supply_Chain/Transparency_in_Supply_Chain_Statement.pdf">Transparency in Supply Chain Statement</Link>,
            <Link href="https://cscworkday.blob.core.windows.net/hrforms/Recruiting/Career_Site/Standards_Policies_Manuals/Social_Responsibility_Manual_for_Manufacturing_Partners.pdf">Vendor Social Responsibility Manual</Link>,
            <Link href="https://cscworkday.blob.core.windows.net/hrforms/Recruiting/Career_Site/Standards_Policies_Manuals/CSC_Foreign_Migrant_Worker_Policy.pdf">Foreign Migrant Worker Policy</Link>,
            <Link href="https://cscworkday.blob.core.windows.net/hrforms/Recruiting/Career_Site/Standards_Policies_Manuals/CSC_CM_Policy_Guideline_English.pdf">Chemical Management Policy</Link>,
          ]}
        />
        <TCell>See "Risk Factors" in our Fiscal Year 2024 Form 10-K</TCell>
        <TCell><Link href="https://investor.columbia.com/sec-filings/all-sec-filings/content/0001050797-25-000023/colm-20241231.htm?TB_iframe=true&height=auto&width=auto&preload=false">Link to 10-K</Link></TCell>
      </View>,
    ],
    [
      'Raw Materials Sourcing',
      '(1) Amount of priority raw materials purchased, by material, and (2) amount of each priority raw material that is certified to a third-party environmental or social standard, by standard',
      'Quantitative',
      'Metric Tonnes (t)',
      'CG-AA-440a.4',
      <View>
        <TCell>Responsible Down Standard: 100%</TCell>
        <TCell>Leather Working Group (LWG) Audited Facilities (FY 2024): 100%</TCell>
        <TCell><Link href="/corporate-responsibility/impact/2024/sustaining-places#preferred-materials">Preferred Materials and Attributes</Link></TCell>
      </View>,
    ],
  ];

  const activityCols = [
    { label: 'Activity Metrics', flex: 3 },
    { label: 'Category', flex: 1 },
    { label: 'Unit of Measure', flex: 1 },
    { label: 'Code', flex: 1 },
    { label: 'Reference / Response', flex: 2.5 },
  ];
  const activityRows = [
    [
      'Number of (1) Tier 1 suppliers and (2) suppliers beyond Tier 1',
      'Quantitative',
      'Number',
      'CG-AA-000.A',
      <View>
        <TCell>Tier 1: 330 facilities; Tier 2: 482 facilities</TCell>
        <TCell><Link href="https://www.columbiasportswearcompany.com/corporate-responsibility-group/responsible-practices/supply-chain/">Transparency Map</Link></TCell>
      </View>,
    ],
  ];

  // ── Appendix B tables ────────────────────────────────────────────────────
  const facilitiesRows = [
    ['Retail', 'Includes CSC owned or operated stores globally.'],
    [
      'Distribution Centers',
      'Includes CSC owned or operated DCs globally that are not contract-based or third-party operated.',
    ],
    [
      'Storage Facilities',
      'Includes on-site and off-site storage locations globally that are not third-party operated.',
    ],
    ['Offices', 'Includes CSC owned or operated offices globally.'],
    [
      'Corporate Housing',
      'CSC leases a small number of corporate housing locations internationally to assist employees during temporary relocations.',
    ],
    [
      'Data Centers',
      'Data centers are operated on a contractual basis and are therefore not included in CSC\'s Scope 1 and 2 footprint.',
    ],
    [
      'Temporary Retail Locations ("Pop-Ups")',
      'Pop-Up Agreements (located in China, Japan, and South Korea) operate for days or weeks and cover a limited area within any retail shop (e.g., one aisle within a department store). Due to the immaterial lease duration and square footage, Pop-Ups are not included in CSC\'s Scope 1 and 2 footprint.',
    ],
  ];

  const emissionsFactorsRows = [
    ['Scope 1', 'Natural Gas', 'EPA Greenhouse Gas Emission Factors Hub, Table 1 (Stationary Combustion)'],
    [
      'Scope 1',
      'Diesel',
      'EPA Greenhouse Gas Emission Factors Hub, Table 8 (Passenger Car, Light-duty Truck, Medium- and Heavy-Duty Truck)',
    ],
    [
      'Scope 1',
      'Refrigerant',
      'Custom emissions factors based on actual refrigerant leak rates from the Company\'s HVAC units.',
    ],
    ['Scope 1', 'Propane', 'EPA Greenhouse Gas Emission Factors Hub, Table 1 (Stationary Combustion)'],
    [
      'Scope 1',
      'Gasoline',
      'EPA Greenhouse Gas Emission Factors Hub, Table 8 (Passenger Car, Light-duty Truck, Medium- and Heavy-Duty Truck)',
    ],
    ['Scope 2', 'Electricity (US) – location-based', 'EPA Emissions & Generation Resource Integrated Database (eGRID)'],
    ['Scope 2', 'Electricity (US) – market-based', 'Green-e® Residual Mix (CO2 only), eGRID (CH4 and N2O)'],
    ['Scope 2', 'Electricity (Canada) – location- & market-based', 'Canada\'s National Inventory Report (NIR)'],
    ['Scope 2', 'Electricity (Europe) – location-based', 'International Energy Agency (IEA) World Electricity Emissions Factors'],
    ['Scope 2', 'Electricity (Europe) – market-based', 'Association of Issuing Bodies (AIB) European Residual Mix (CO2 only), IEA (CH4 and N2O)'],
    ['Scope 2', 'Electricity (all other locations) – location- & market-based', 'International Energy Agency (IEA) World Electricity Emissions Factors'],
  ];

  const activityDataRows = [
    ['Scope 1', 'Refrigerant Leakage', 'Primary & Secondary'],
    ['Scope 1', 'Natural Gas', 'Primary & Secondary'],
    ['Scope 1', 'Diesel', 'Primary'],
    ['Scope 1', 'Propane', 'Primary'],
    ['Scope 1', 'Gasoline', 'Primary'],
    ['Scope 2', 'Electricity', 'Primary & Secondary'],
  ];

  const assumptionsRows = [
    [
      'Natural Gas',
      <View>
        <TBold>Primary:</TBold>
        <TBullets
          items={[
            'For storage facilities that are associated with retail stores and do not have lease dates available, the lease term from the retail site is associated with the storage location.',
          ]}
        />
        <TBold>Gap-fill:</TBold>
        <TBullets
          items={[
            'Previously assigned annual EUIs for each facility are provided in kWh/sqft. Total natural gas energy use (in kWh) is calculated by multiplying the annual EUI by total square footage.',
          ]}
        />
        <TCell>Country-specific natural gas consumption patterns from the IEA are for the following regional assumptions are utilized in calculations:</TCell>
        <TBullets
          items={[
            <>{'In Korea, 43% of natural gas supply reaches final consumers for residential, commercial, and industrial applications.'}<Text style={{ fontSize: 4 }}> </Text><Text style={{ fontSize: 5, verticalAlign: 'super', fontWeight: 'bold' }}>10</Text>{' In 2022, the residential and commercial sectors accounted for 26% of total natural gas end use consumption.'}<Text style={{ fontSize: 4 }}> </Text><Text style={{ fontSize: 5, verticalAlign: 'super', fontWeight: 'bold' }}>11</Text>{' As a common fuel for indoor heating, we assume all Korea facilities combust natural gas.'}</>,

            <>{'In Japan, 34% of natural gas supply reaches final consumers for residential, commercial, and industrial applications.'}<Text style={{ fontSize: 4 }}> </Text><Text style={{ fontSize: 5, verticalAlign: 'super', fontWeight: 'bold' }}>12</Text>{' In 2022, the residential and commercial sectors accounted for 20% of total natural gas end use consumption.'}<Text style={{ fontSize: 4 }}> </Text><Text style={{ fontSize: 5, verticalAlign: 'super', fontWeight: 'bold' }}>13</Text>{' As a common fuel for indoor heating, we assume all Japan facilities consume natural gas.'}</>,

            <>{'In China, 75% of natural gas supply reaches final consumers for residential, commercial, and industrial applications.'}<Text style={{ fontSize: 4 }}> </Text><Text style={{ fontSize: 5, verticalAlign: 'super', fontWeight: 'bold' }}>14</Text>{' In 2023, after industrial uses, the residential and commercial sector was the largest consumer of natural gas in China.'}<Text style={{ fontSize: 4 }}> </Text><Text style={{ fontSize: 5, verticalAlign: 'super', fontWeight: 'bold' }}>15</Text>{' As a common fuel across China, we assume all China facilities consume natural gas.'}</>,

            <>{'In Europe, 62% of natural gas supply reaches final consumers for residential, commercial, and industrial applications.'}<Text style={{ fontSize: 4 }}> </Text><Text style={{ fontSize: 5, verticalAlign: 'super', fontWeight: 'bold' }}>16</Text>{' In 2022, the residential and commercial sector used more natural gas than the electricity generating sector. As a common fuel across Europe, we assume all Europe facilities consume natural gas.'}</>,

            <>{'In Canada, 43% of natural gas supply reaches final consumers for residential, commercial, and industrial applications.'}<Text style={{ fontSize: 4 }}> </Text><Text style={{ fontSize: 5, verticalAlign: 'super', fontWeight: 'bold' }}>17</Text>{' In 2022, the residential and commercial sectors accounted for roughly 23% of total natural gas end use consumption. As a common fuel for indoor heating, we assume all Canada facilities consume natural gas.'}</>,

          ]}
        />
      </View>,
    ],
    [
      'Propane',
      <View>
        <TBold>Primary and Secondary:</TBold>
        <TBullets
          items={[
            'Only one site in CSC\'s facility list reports propane usage. As a result, it is assumed that propane usage is not an average day-to-day practice, and therefore a gap-filling exercise was not performed on other global facilities.',
          ]}
        />
      </View>,
    ],
    [
      'Mobile Combustion (North America)',
      <View>
        <TBold>Primary:</TBold>
        <TBullets items={['Actual milage for each vehicle was not tracked for 2022 or 2024.']} />
        <TBold>Secondary:</TBold>
        <TBullets
          items={[
            'CSC, with the aid of Facility Managers, reviewed the list of vehicles and determined a reasonable annual mileage for each vehicle, based on primary use and type of the vehicle.',
          ]}
        />
      </View>,
    ],
    [
      'Mobile Combustion (Europe)',
      <View>
        <TBold>Primary:</TBold>
        <TBullets items={['Actual mileage for each vehicle was not tracked for 2022 or 2024.']} />
        <TBold>Secondary:</TBold>
        <TBullets
          items={[
            'CSC identified the maximum of kilometers (Km) per the vehicle\'s lease agreement and divided it by the lease term to obtain an annual distance.',
          ]}
        />
      </View>,
    ],
    [
      'Equipment',
      <View>
        <TBold>Primary:</TBold>
        <TBullets
          items={[
            'CSC\'s Distribution Centers (DC) are the only facility type that use equipment during day-to-day operations. Data provided by DC managers are only applicable to those specific DCs, and are not extrapolated to the rest of CSC\'s Scope 1 & 2 footprint.',
          ]}
        />
      </View>,
    ],
    [
      'Fugitive Emissions (Refrigerant Leakage)',
      <View>
        <TBold>Primary:</TBold>
        <TBullets
          items={[
            'Refrigerant leakage (lbs) was not tracked for 2022 or 2024.',
            'A list of over 500 HVAC units across North America was provided. Charge capacities of these units were obtained through individual user manuals.',
          ]}
        />
        <TBold>Secondary:</TBold>
        <TBullets
          items={[
            'Where charge capacity was not listed for a unit, the average charge capacity of all listed units (~16.9lbs refrigerant) was used.',
            <>{'Unless otherwise determinable, HVAC units and AC systems were assumed to be "Residential and Commercial A/C including Heat Pumps" per EPA classification'}<Text style={{ fontSize: 4 }}> </Text><Text style={{ fontSize: 5, verticalAlign: 'super', fontWeight: 'bold' }}>18</Text>{'.'}</>,

            'Where Refrigerant/ASHRAE # was not listed, the assumed refrigerant was R410A (the replacement for R22 mandated under the U.S. Clean Air Act and the Montreal Protocol on Substances that Deplete the Ozone Layer).',
            'Only GHGs listed in the Kyoto protocol should be included in CSC\'s Scope 1 & 2 inventory. HCFC-22 (R-22) is not a listed gas in the Kyoto Protocol, and as such, is excluded.',
          ]}
        />
      </View>,
    ],
    [
      'Electricity',
      <View>
        <TBold>Primary:</TBold>
        <TBullets
          items={[
            'For storage facilities that are associated with retail stores and do not have lease dates available, the lease term from the retail site is associated with the storage location.',
          ]}
        />
        <TBold>Gap-fill:</TBold>
        <TBullets
          items={[
            'Previously assigned annual EUIs for each facility are provided in kWh/sqft. Total natural gas energy use (in kWh) is calculated by multiplying the annual EUI by total square footage.',
          ]}
        />
      </View>,
    ],
  ];

  return (
    <>
      {/* ── Appendix A: SASB ──────────────────────────────────────────────── */}
      <PageTemplate sectionTitle="Appendices">
        <Section>
          <H1>Appendix A: SASB</H1>
          <TableCaption>Table 1. Sustainability Disclosure Topics & Metrics - Apparel, Accessories & Footwear</TableCaption>
          <DataTable columns={sasbCols} rows={sasbRows} />
          <TableCaption>Table 2. Activity Metrics - Apparel, Accessories & Footwear</TableCaption>
          <DataTable columns={activityCols} rows={activityRows} />
        </Section>
      </PageTemplate>

      {/* ── Appendix B: GHG Emissions Methodology ────────────────────────── */}
      <PageTemplate sectionTitle="Appendices">
        <Section>
          <H1>Appendix B: Greenhouse Gas Emissions Methodology</H1>
          <H3>Statement on revisions to 2022 GHG Emissions Disclosure</H3>
          <Body>
            Columbia Sportswear Company has recalculated the Company's prior 2022 corporate emissions inventory to reflect the most recent global warming potentials (AR6) published by the IPCC. An update to AR6 GWPs resulted in a 3% increase in the relative warming effects of N2O and a 19% increase in the relative warming effects of refrigerant R410A from the Company's prior emissions inventories. Other changes include an update to the Company's methodologies for extrapolating energy usage to reflect industry best practices and standards.
          </Body>
          </Section>
          </PageTemplate>
          <PageTemplate sectionTitle="Appendices">
          <Section>
          <Text style={{ fontSize: 16, lineHeight: 1.4, marginVertical: 12, fontWeight: 700}}>
            TOTAL EMISSIONS — ANNUAL (2022 & 2024){'\n'}GHG EMISSIONS (metric tons CO2e)
          </Text>
          <Image src={ghgTotalImg} style={[styles.chartImage, {width: '70%'}]} />
          <Small>*Revised numbers, based on updated calculation factors</Small>

          <H3>Scope 1 & 2 GHG Emissions Methodology</H3>
          <H4>Standards</H4>
          <Body>This report has been prepared in accordance with the relevant Greenhouse Gas (GHG) Protocol Standards, including:</Body>
          <BulletList
            items={[
              'GHG Protocol Corporate Accounting and Reporting Standard (WBCSD & WRI, 2004)',
              'GHG Protocol Scope 2 Guidance (WBCSD & WRI, 2015)',
              'GHG Protocol Corporate Value Chain (Scope 3) Accounting and Reporting Standard (WBCSD & WRI, 2011)',
            ]}
          />

          </Section>
          </PageTemplate>
          <PageTemplate sectionTitle="Appendices">
          <Section>
          <H4>Timeframe of Reporting</H4>
          <Body>
            CSC reports emissions on a calendar year basis in line with its financial reporting year. GHG emissions in this report reflect business activities taking place January 1 - December 31 for the years 2022 and 2024.
          </Body>

          <H4>Organizational Boundary</H4>
          <Body>
            In accordance with the GHG Protocol, CSC includes emissions from operations over which the Company has operational control (i.e., CSC is responsible for accounting for GHG emissions from its locations (whether leased or owned) for which it has direct control over operations). In retail locations, CSC assumes operational control at sites where CSC (1) operates the lease, (2) owns all inventory, and (3) controls pricing. The footprint represents all owned operations or operations for which CSC has operational control, even when not owned by CSC.
          </Body>

          <H4>Facilities</H4>
          <DataTable
            columns={[
              { label: 'Facility Type', flex: 1.2 },
              { label: 'Scope Description', flex: 4 },
            ]}
            rows={facilitiesRows}
          />
        </Section>
      </PageTemplate>

      <PageTemplate sectionTitle="Appendices">
        <Section>
          <H4>Emissions Factors</H4>
          <Body>This table outlines the emission factor sources used in FY22 and FY24 emissions calculations.</Body>
          <DataTable
            columns={[
              { label: 'Emissions Source', flex: 1 },
              { label: 'Emission Source Type', flex: 2 },
              { label: 'Emission Factor Employed', flex: 3 },
            ]}
            rows={emissionsFactorsRows}
          />

          <H4>Activity Data</H4>
          <Body>
            CSC aims to collect as much high-quality, primary (actual) data across its activities as possible. However, primary data may not be available for some activities or may not be of sufficient quality. In those cases, secondary data (estimation, gap fill, or extrapolation) may be used. The chart below identifies whether the calculations were based on primary or secondary data.
          </Body>
          <DataTable
            columns={[
              { label: 'Emissions Source', flex: 1 },
              { label: 'Activity Data', flex: 2 },
              { label: 'Data type (actual vs. gap-fill)', flex: 2 },
            ]}
            rows={activityDataRows}
          />

          <H4>Global Warming Potential Values</H4>
          <Body>
            The Greenhouse Gas Emissions inventory in this report was calculated using Global Warming Potential (GWP) values aligned with the Sixth Annual Report as published by the Intergovernmental Panel on Climate Change (IPCC).
          </Body>

          <H4>Renewable Energy Certificates</H4>
          <Body>In 2024, CSC purchased 5,882 MWh of renewable energy certificates (RECs).<Text style={{ fontSize: 6 }}> </Text><Text style={{ fontSize: 8, verticalAlign: 'super', fontWeight: 'bold' }}>7</Text></Body>

          <H4>Management's Significant Assumptions</H4>
          <Body>
            Natural gas applies gap filling methodology for all sites and all regions, unless explicitly communicated that a site does not combust natural gas.
          </Body>
          <Body>Electricity applies gap filling methodology for all sites and regions.</Body>
          <Body>Fugitive emissions (refrigerant leakage) applies gap filling methodology for all sites and regions.</Body>
          <Body>
            If a facility reports energy consumption data for only a portion of its operating period (e.g., a retail location that only reports energy usage for 6 of its 12-month operating lease), missing data are gap-filled (i.e., extrapolated) using daily annual average from the reported energy consumption data.
          </Body>
          <Body>
            If a US facility does not report any energy usage during the year, Scope 1 and 2 activity data (e.g., natural gas and/or electricity) are estimated using energy use intensities (EUIs) provided by the Commercial Buildings Energy Consumption Survey (CBECS<Text style={{ fontSize: 6 }}> </Text><Text style={{ fontSize: 8, verticalAlign: 'super', fontWeight: 'bold' }}>8</Text>) via US census region or division.
          </Body>
          <Body>
            If a non-US facility does not report any energy usage during the year, Scope 1 and 2 activity data are estimated using climate zones defined by the American Society of Heating, Refrigerating and Air-Conditioning Engineers (ASHRAE<Text style={{ fontSize: 6 }}> </Text><Text style={{ fontSize: 8, verticalAlign: 'super', fontWeight: 'bold' }}>9</Text>) via latitude-longitude coordinates.
          </Body>
          <Body>
            The below chart outlines Management's Significant Assumptions both for the primary data used, and for the gap fill performed, where primary data was not available (i.e., secondary data) in this Scope 1 and 2 footprint.
          </Body>
        </Section>
      </PageTemplate>

      <PageTemplate sectionTitle="Appendices">
        <Section>
          <DataTable
            columns={[
              { label: 'Emission Source', flex: 1.4 },
              { label: 'Management\u2019s Assumptions', flex: 5 },
            ]}
            rows={assumptionsRows}
          />

          <H4>Review</H4>
          <Body>
            In 2024, the Company underwent pre-assurance of 2024 scope 1 and 2 data by an outside third party. The calculation methodology, including the scientific basis for underlying assumptions and emissions factors leveraged within the calculations, were reviewed by an independent auditor for alignment with GHG Protocol standards and guidelines.
          </Body>

          <H4>Uncertainty</H4>
          <Body>
            GHG emissions quantification is subject to significant inherent measurement uncertainty because of such things as GHG emissions factors that are used in mathematical models to calculate GHG emissions, and the inability of these models, due to incomplete scientific knowledge and other factors, to accurately measure under all circumstances the relationship between various inputs and the resultant GHG emissions. Environmental and energy use data used in GHG emissions calculations are subject to inherent limitations, given the nature and the methods used for measuring such data. The selection by management of different but acceptable measurement techniques could have resulted in materially different amounts or metrics being reported.
          </Body>
          <Body>
            The preparation of the energy consumption metrics requires management to establish the criteria, make determinations as to the relevancy of information to be included, and make assumptions that affect reported information. The selection by management of different but acceptable measurement techniques could have resulted in materially different amounts or metrics being reported.
          </Body>
        </Section>
      </PageTemplate>

      {/* ── Appendix C ───────────────────────────────────────────────────── */}
      <PageTemplate sectionTitle="Appendices">
        <Section>
          <H1>Appendix C: Social Responsibility Audit & Assessment Types</H1>
          <Body>
            CSC utilizes several different types of audits and assessments to gather data that is used to analyze and measure our suppliers' adherence to and performance against our Standards of Manufacturing Practices (SMP) and social responsibility requirements:
          </Body>

          <H4>Internal Corporate Responsibility (CR) Field Team</H4>
          <Body>
            All audits performed by our internal CR staff are unannounced. This allows our Specialists to see the manufacturing partner facility conditions as they are on a typical day. As illustrated in the Audits & Assessments Conducted chart, the number of audits performed by our team greatly decreased in the past few years, which is due to a combination of increased adoption of SLCP, Better Work assessments, and the recognition of other assessments. This enables our field team to spend more time on remediation, training, capability building, and worker empowerment programs with our manufacturing partners.
          </Body>

          <H4>Third Party Audit Firms</H4>
          <Body>
            For a handful of factories where we do not have local staff, as well as for our licensee business, we employ both Omega Compliance and Elevate Limited to conduct unannounced audits on our behalf, according to our SMP audit protocol. We do regular trainings with both firms and perform intermittent shadow audits to ensure audit quality.
          </Body>

          <H4>Better Work Program</H4>
          <Body>
            As a Better Work partner, we encourage our manufacturing partners who operate in countries where Better Work operates to join the program. In 2024, 81 of our finished goods factories were enrolled in the Better Work program. We incorporate the Better Work assessment results into our database for data tracking and scorecard rating.
          </Body>

          <H4>Social & Labor Convergence Program (SLCP)</H4>
          <Body>
            In 2024, 215 manufacturing partners (Tier 1 and Tier 2) completed the self-assessment plus verification process (as compared to 221 in 2023). We use these verified assessment results to assign our own rating according to our system and require the factory to complete their Corrective Action Plan in our Corporate Responsibility Database (Gateway).
          </Body>

          <H4>External, Other Standards</H4>
          <Body>
            We have developed strong relationships with several brands that share our values and approach to factory monitoring, and who accept audit results performed against their standards. We also recognize a handful of other credible audit, assessment and certification schemes in an effort to reduce audit fatigue. We translate performance data into our own rating system and require factories to complete their Corrective Action Plan in our Corporate Responsibility Database (Gateway).
          </Body>
          </Section>
          </PageTemplate>
          <PageTemplate sectionTitle="Appendices">
          <Section>

          <H3>Social Responsibility Manufacturing Partner Facility Audits & Assessments Conducted</H3>
          <Image src={auditsAndAssessmentsImg} style={ [styles.chartImage, { marginBottom: 16, width: '60%' }]} />
          <Body>
            In 2024, we worked with 330 finished goods factories (Tier 1), of which 87% had at least one audit or assessment performed during the year. Our aim is to ensure that all Tier 1 facilities receive at least one audit or assessment annually. Notably, some factories may undergo multiple audits in a calendar year based upon the performance of the previous audit. Additionally, we continue to expand our social responsibility program to cover our processing facilities (Tier 2), employing the SLCP assessment. In 2024, 56 Tier 2 facilities underwent an SLCP verified assessment, as compared to 69 in 2023.
          </Body>
          <Body>
            CSC maintains an internal Corporate Responsibility database known as "Gateway," which contains factory profile information, Standards of Manufacturing Practices (SMP) audit/assessment results, remediation tracking, training and capability building activities, and factory SMP scorecard performance. Our factory partners can access a portal to input their remediation Corrective Action Plans and view their performance scores. This data enables us to conduct performance and trend analysis at the individual factory level and aggregate the data to highlight supply chain trends, thereby informing long-term strategy and identifying areas for improvement.
          </Body>
        </Section>
      </PageTemplate>

      {/* ── Appendix D ───────────────────────────────────────────────────── */}
      <PageTemplate sectionTitle="Appendices">
        <Section>
          <H1>Appendix D: Corporate Responsibility Scorecard Rating System</H1>
          <Body>
            To assess the performance of our manufacturing partner facilities in terms of social responsibility, environmental sustainability, and product compliance, CSC has developed a rating system known as the Corporate Responsibility (CR) Scorecard Rating System. This system primarily evaluates Tier 1 manufacturing partner facilities. A comprehensive CR Scorecard Rating, or CR Grade, is determined only when grades are assigned for all three key areas: social responsibility, environmental sustainability, and product compliance. Each area carries equal weight in the total CR Grade calculation. In 2024, 139 Tier 1 manufacturing partner facilities received a CR Scorecard Grade.
          </Body>
          <BulletList
            items={[
              'Social Responsibility Scorecard Rating System',
              'Product Compliance Scorecard Rating System',
              'Environmental Responsibility Scorecard Rating System',
            ]}
          />
          </Section>
          </PageTemplate>
          <PageTemplate sectionTitle="Appendices">
          <Section>
          <Text style={{ fontSize: 16, lineHeight: 1.4, marginVertical: 12, fontWeight: 700}}>2024 Tier 1 Manufacturing Partner CR Scorecard Grades</Text>
          <Image src={scorecardGradesImg} style={[styles.chartImage, { width: '60%' }]} />
        </Section>
      </PageTemplate>

      {/* ── Appendix E ───────────────────────────────────────────────────── */}
      <PageTemplate sectionTitle="Appendices">
        <Section>
          <H1>Appendix E: About this Report</H1>
          <H3>Reporting Scope</H3>
          <Body>
            This Impact Report covers Columbia Sportswear Company's 2024 fiscal year (January 1, 2024 through December 31, 2024).
          </Body>

          <H3>Data Integrity</H3>
          <Body>
            Impact data is shaped by evolving methodologies, emerging standards, and data availability. Adapting to these changes while maintaining comparability in our data is critical to the integrity of the data and related insights. To that end, we have been focused on designing tighter controls governing our impact data processes and systems. Data presented in this Report has been collected, reviewed, and, in some cases, validated by our internal audit function, for completeness and accuracy or was subject to pre-assurance processes by an external third-party. It represents the most complete and accurate information at the time of publication.
          </Body>

          <H3>Disclosure Practices</H3>
          <Body>
            Columbia Sportswear Company is committed to publicly reporting its performance on environmental and social sustainability topics.
          </Body>

          <H3>Safe Harbor</H3>
          <Body>
            The information in this Report and our corporate responsibility/sustainability reporting and website, inclusive of charts, graphs and discussion, and all other information presented, may contain forward-looking statements, estimates, or projections based on expectations as of the original date of those materials. Those statements, estimates and projections are not guarantees of future results or performance and are subject to certain known and unknown risks and uncertainties that are difficult to predict, are often beyond our control and could cause actual results to differ materially. These risks and uncertainties are detailed in our reports filed with the U.S. Securities and Exchange Commission (SEC), including our Annual Report on Form 10-K. Presented information may also discuss previously non-public financial and statistical information. Our approach to the disclosures included in this Report and our corporate responsibility/sustainability reporting and website differs from our approach to the disclosures we include in our mandatory regulatory reports, including our filings with the SEC. This report and our corporate responsibility/sustainability reporting and website are intended to provide information from a different perspective and, in certain cases, in more detail than that required to be included in our filings with the SEC. Unless otherwise specified, all information (including forward-looking information) speaks only as of the date on which it is made, and we disclaim any obligation to update or delete any outdated information contained in this report or in our website materials. This Report contains third-party data. In the majority of circumstances, we have not, and do not intend to, independently verify third-party data. Actual results and outcomes may differ from those expressed in or implied in this report due to, among other factors, any applicable legal requirements and/or industry standards, in providing such data.
          </Body>
        </Section>
      </PageTemplate>

      {/* ── Footnotes ────────────────────────────────────────────────────── */}
      <PageTemplate sectionTitle="Appendices">
        <Section>
          <H3>Footnotes</H3>
          <Footnote num={7} text="2024 REC retirements are reflected in CSC's 2023 market-based scope 2 emissions total." />
          <Footnote num={8} text={'"2018 Commercial Buildings Energy Consumption Survey final results" by US EIA: https://www.eia.gov/consumption/commercial/'} />
          <Footnote num={9} text={'"Climatic Data for Building Design Standards" by ANSI/ASHRAE: https://www.ashrae.org/file%20library/technical%20resources/standards%20and%20guidelines/standards%20addenda/169_2020_a_20211029.pdf. Addendum, Tables A-5 (Canadian Stations and Climate Zones) and A-6 (International Stations and Climate Zones)'} />
          <Footnote num={10} text={'"How is natural gas used in Korea?" by IEA: https://www.iea.org/countries/korea/natural-gas. Accessed on 10/11/2024.'} />
          <Footnote num={11} text={'"Korea Natural Gas Security Policy" by IEA https://www.iea.org/articles/korea-natural-gas-security-policy. Accessed on 10/11/2024.'} />
          <Footnote num={12} text={'"How is natural gas used in Japan?" by IEA: https://www.iea.org/countries/japan/natural-gas. Accessed on 10/11/2024.'} />
          <Footnote num={13} text={'"Japan Natural Gas Security Policy" by IEA: https://www.iea.org/articles/japan-natural-gas-security-policy. Accessed on 10/11/2024.'} />
          <Footnote num={14} text={'"How is natural gas used in China?" by IEA: https://www.iea.org/countries/china/natural-gas. Accessed on 10/11/2024.'} />
          <Footnote num={15} text={'"Chinese natural gas consumption by sector, 2005-2023" by IEA: https://www.iea.org/data-and-statistics/charts/chinese-natural-gas-consumption-by-sector-2005-2023. Accessed on 10/15/2024.'} />
          <Footnote num={16} text={'"How is natural gas used in Europe?" by IEA: https://www.iea.org/regions/europe/natural-gas. Accessed on 10/15/2024.'} />
          <Footnote num={17} text={'"How is natural gas used in Canada?" by IEA: https://www.iea.org/countries/canada/natural-gas. Accessed on 10/15/2024.'} />
          <Footnote num={18} text={'"Accounting Tool to Support Federal Reporting of Hydrofluorocarbon Emissions, Appendix B" by USA EPA: https://www.epa.gov/sites/default/files/2015-09/documents/hfc_emissions_accounting_tool_supporting_documentation.pdf'} />
        </Section>
      </PageTemplate>
    </>
  );
}

// ---------------------------------------------------------------------------
// Section composite — cover + content (for use in the full assembled report)
// ---------------------------------------------------------------------------
export function AppendicesSection() {
  return (
    <>
      <SectionCover
        bgImage={appendixCoverImg}
        accentColor={COLORS.navy}
        title="Appendices"
      />
      <AppendicesPages />
    </>
  );
}

// ---------------------------------------------------------------------------
// Standalone Document (used by the generate script)
// ---------------------------------------------------------------------------
export default function AppendicesPDF({ pageOffset = 0, totalDocPages = null } = {}) {
  return (
    <PageOffsetContext.Provider value={{ pageOffset, totalDocPages }}>
      <Document
        title="2024 CSC Impact Report — Appendices"
        author="Columbia Sportswear Company"
      >
        <AppendicesSection />
      </Document>
    </PageOffsetContext.Provider>
  );
}
